import { motion } from "framer-motion";
import { useState } from "react";
import { Mail, Phone, MapPin, ArrowUpRight, Clock, Copy, Check } from "lucide-react";
import { CONTACT, NAV_ITEMS } from "../../data/content";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

const EmailBlock = () => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select the text node so user can ctrl/cmd-c
      const range = document.createRange();
      const node = document.getElementById("contact-email-text");
      if (node) {
        range.selectNodeContents(node);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
  };

  return (
    <motion.div
      data-testid="contact-email-block"
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className="col-span-12 md:col-span-7 relative bg-white border border-ink/10 p-8 md:p-12 hover:border-ink/30 transition-all"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="overline text-ink-soft mb-3">Write to us</div>
          <span
            id="contact-email-text"
            data-testid="contact-email-text"
            className="font-display font-medium text-ink break-all leading-[1.1] cursor-text"
            style={{
              fontSize: "clamp(1.5rem, 3.2vw, 2.75rem)",
              userSelect: "all",
              WebkitUserSelect: "all",
            }}
          >
            {CONTACT.email}
          </span>
        </div>
        <Mail className="w-6 h-6 md:w-8 md:h-8 text-ink-soft shrink-0" strokeWidth={1.5} />
      </div>

      <p className="mt-8 text-ink-soft max-w-md">
        For technical specifications, certificates of analysis, distribution
        enquiries or sample requests — we usually reply within one business
        day.
      </p>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleCopy}
          data-testid="contact-email-copy-btn"
          className="group inline-flex items-center gap-2 bg-ink text-bone px-4 py-2.5 text-sm font-medium rounded-sm hover:bg-ink/85 transition-all"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4 text-forest-accent" />
              Copied
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copy email
            </>
          )}
        </button>
        <a
          href={`mailto:${CONTACT.email}`}
          data-testid="contact-email-mailto"
          className="group inline-flex items-center gap-1.5 border border-ink/20 text-ink px-4 py-2.5 text-sm font-medium rounded-sm hover:bg-ink/5 transition-colors"
        >
          Open in mail app
          <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
        <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-ink-soft">
          or click the address to select
        </span>
      </div>
    </motion.div>
  );
};

export const Contact = () => {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="scroll-mt-nav relative bg-bone border-t border-ink/10 pt-24 md:pt-32 pb-10 overflow-hidden"
    >
      <div className="absolute inset-0 grid-lines opacity-50 pointer-events-none" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
        >
          <div className="overline text-forest-accent mb-6">/ 07 — Contact</div>
          <h2
            className="font-display font-black tracking-tight leading-[0.85] text-ink"
            style={{ fontSize: "clamp(3.5rem, 13vw, 12rem)" }}
          >
            Let&apos;s
            <br />
            <span className="italic font-light text-amber-accent">talk.</span>
          </h2>
        </motion.div>

        <div className="mt-16 md:mt-24 grid grid-cols-12 gap-8 md:gap-12">
          {/* Email primary */}
          <EmailBlock />

          {/* Side blocks */}
          <div className="col-span-12 md:col-span-5 grid grid-cols-1 gap-px bg-ink/10 border border-ink/10">
            <div className="bg-bone p-6 md:p-8" data-testid="contact-phone-block">
              <Phone className="w-4 h-4 text-amber-accent mb-3" strokeWidth={1.5} />
              <div className="overline text-ink-soft mb-2">Call</div>
              <a
                href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                className="font-display text-lg md:text-xl text-ink hover:text-amber-accent transition-colors"
              >
                {CONTACT.phone}
              </a>
            </div>
            <div className="bg-bone p-6 md:p-8" data-testid="contact-address-block">
              <MapPin className="w-4 h-4 text-amber-accent mb-3" strokeWidth={1.5} />
              <div className="overline text-ink-soft mb-2">Visit</div>
              <address className="not-italic font-display text-base md:text-lg text-ink leading-snug">
                {CONTACT.address.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </address>
            </div>
            <div className="bg-bone p-6 md:p-8" data-testid="contact-hours-block">
              <Clock className="w-4 h-4 text-amber-accent mb-3" strokeWidth={1.5} />
              <div className="overline text-ink-soft mb-2">Hours</div>
              <div className="font-display text-base md:text-lg text-ink">
                {CONTACT.hours}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer
          data-testid="site-footer"
          className="mt-24 md:mt-32 pt-10 border-t border-ink/15 grid grid-cols-12 gap-6 text-sm"
        >
          <div className="col-span-12 md:col-span-4">
            <div className="font-display font-black tracking-tight text-ink text-2xl leading-none">
              Milestone
            </div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-soft mt-1">
              Preservatives Pvt. Ltd.
            </div>
            <p className="mt-4 text-ink-soft text-[13px] leading-relaxed max-w-xs">
              Shelfanox™ antioxidants, NatPro™ tocopherols and Specialty
              Chemicals — manufactured in India, trusted worldwide.
            </p>
          </div>
          <div className="col-span-6 md:col-span-3">
            <div className="overline text-ink-soft mb-4">Navigate</div>
            <ul className="space-y-2">
              {NAV_ITEMS.map((n) => (
                <li key={n.id}>
                  <a
                    href={`#${n.id}`}
                    data-testid={`footer-link-${n.id}`}
                    className="text-ink hover:text-amber-accent transition-colors"
                  >
                    {n.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="col-span-6 md:col-span-3">
            <div className="overline text-ink-soft mb-4">Portfolio</div>
            <div className="space-y-4 text-ink">
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-amber-accent mb-1.5">
                  Shelfanox™
                </div>
                <ul className="space-y-1.5 text-sm">
                  <li>TBHQ</li>
                  <li>BHA</li>
                  <li>BHT</li>
                  <li>Ascorbyl Palmitate</li>
                </ul>
              </div>
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-forest-accent mb-1.5">
                  NatPro™
                </div>
                <ul className="space-y-1.5 text-sm">
                  <li>Mixed Tocopherols</li>
                </ul>
              </div>
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-steel-accent mb-1.5">
                  Specialty
                </div>
                <ul className="space-y-1.5 text-sm">
                  <li>MeHQ</li>
                  <li>Guaiacol</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-2 md:text-right">
            <div className="overline text-ink-soft mb-4">© {new Date().getFullYear()}</div>
            <p className="text-ink-soft text-[12px] leading-relaxed">
              All rights reserved.
              <br />
              {/* NatPro™ is a trademark of Milestone Preservatives Pvt. Ltd. */}
            </p>
          </div>
        </footer>
      </div>
    </section>
  );
};
